import React, { useState, useEffect, useMemo } from 'react';
import TopBar from '../../../layout/TopBar';
import DamiaSidebar from '../components/DamiaSidebar';
import ChatArea from '../components/ChatArea';
import { Conversation, Message } from '../types';
import { useAuth } from '../../../context/AuthContext';
import { apiClient } from '../../../utils/apiClient';
import { getWelcomeMessage } from '../welcomeMessages';
import { useDamiaThinking } from '../hooks/useDamiaThinking';

const Damia: React.FC = () => {
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isThinking, setIsThinking] = useState(false);
    const [isCreatingConversation, setIsCreatingConversation] = useState(false);
    const [welcomeMessageIndex, setWelcomeMessageIndex] = useState(0);
    const [typingTitle, setTypingTitle] = useState<{ conversationId: string; title: string } | null>(null);

    const { isAuthenticated, logout, user } = useAuth();
    const { thinkingSteps, isThinkingActive, resetThinking } = useDamiaThinking(currentConversationId);

    // Extraire le prénom de l'utilisateur
    const firstName = useMemo(() => {
        if (!user?.name) return '';
        return user.name.split(' ')[0];
    }, [user?.name]);

    // Fonction helper pour mapper les enrichissements (inchangée)
    const mapEnrichmentResults = (enrichmentResults: any[]) => {
        const mappedResults: any[] = [];

        enrichmentResults.forEach((enrichment: any) => {
            if (enrichment.data && enrichment.data.results && Array.isArray(enrichment.data.results)) {
                enrichment.data.results.forEach((result: any) => {
                    mappedResults.push({
                        source: result.source,
                        title: result.title,
                        content: result.snippet,
                        link: result.url,
                        date: result.date,
                        jurisdiction: result.jurisdiction,
                        chamber: result.chamber,
                        reference: result.reference,
                        themes: result.themes,
                        relevanceScore: result.relevanceScore,
                        ecli: result.ecli,
                        solution: result.solution,
                        legalDomain: result.legalDomain,
                        subDomains: result.subDomains,
                        issues: result.issues
                    });
                });
            } else {
                if (enrichment.source === 'jurisprudence' && enrichment.data?.id) {
                    mappedResults.push({
                        source: 'jurisprudence',
                        title: `Jurisprudence ${enrichment.data.id.slice(-8)}`,
                        content: `Jurisprudence référencée (ID: ${enrichment.data.id})`,
                        link: `/jurisprudences/${enrichment.data.id}`,
                        relevanceScore: enrichment.relevanceScore || enrichment.data.relevanceScore,
                        isSimplified: true,
                        jurisprudenceId: enrichment.data.id
                    });
                } else {
                    mappedResults.push({
                        source: enrichment.source,
                        title: enrichment.data?.title || enrichment.data?.reference || 'Résultat',
                        content: enrichment.data?.snippet || enrichment.data?.content || '',
                        link: enrichment.data?.url || '',
                        date: enrichment.data?.date,
                        jurisdiction: enrichment.data?.jurisdiction,
                        chamber: enrichment.data?.chamber,
                        reference: enrichment.data?.reference,
                        themes: enrichment.data?.themes,
                        relevanceScore: enrichment.data?.relevanceScore,
                        ecli: enrichment.data?.ecli,
                        solution: enrichment.data?.solution,
                        legalDomain: enrichment.data?.legalDomain,
                        subDomains: enrichment.data?.subDomains,
                        issues: enrichment.data?.issues
                    });
                }
            }
        });

        return mappedResults;
    };

    // Fonction pour enrichir une jurisprudence par son ID via l'API
    const fetchJurisprudenceDetails = async (jurisprudenceId: string) => {
        if (!isAuthenticated) return null;

        try {
            const response = await apiClient(`/jurisprudences/${jurisprudenceId}`);

            if (!response.ok) return null;

            const data = await response.json();
            if (data.success && data.data) {
                const juris = data.data;
                return {
                    source: 'jurisprudence' as const,
                    title: juris.titlesAndSummaries?.[0]?.titles?.[0] || `Décision du ${juris.decision_date}`,
                    content: juris.titlesAndSummaries?.[0]?.summary || '',
                    relevanceScore: 0, // Sera remplacé par le score original
                    isSimplified: false,
                    jurisprudenceId: juris.jurisprudenceId,
                    jurisdiction: juris.jurisdiction,
                    chamber: juris.chamber,
                    number: juris.number,
                    decision_date: juris.decision_date,
                    solution: juris.solution,
                    legalDomain: juris.themes?.[0] || '',
                    issues: juris.themes || [],
                    hasBaseData: true,
                    hasMetadata: false
                };
            }
            return null;
        } catch (error) {
            console.error(`Erreur enrichissement jurisprudence ${jurisprudenceId}:`, error);
            return null;
        }
    };

    // Fonction pour enrichir toutes les jurisprudences simplifiées d'un message
    const enrichSimplifiedJurisprudences = async (searchResults: any[]) => {
        if (!searchResults || searchResults.length === 0) return searchResults;

        const enrichmentPromises = searchResults.map(async (result) => {
            if (result.source === 'jurisprudence' && result.isSimplified && result.jurisprudenceId) {
                const enrichedData = await fetchJurisprudenceDetails(result.jurisprudenceId);
                if (enrichedData) {
                    return {
                        ...enrichedData,
                        relevanceScore: result.relevanceScore // Conserver le score original
                    };
                }
            }
            return result;
        });

        return Promise.all(enrichmentPromises);
    };

    // Fonction pour charger les enrichissements complets (utilisée au rechargement)
    const loadEnrichedJurisprudences = async (messageId: string) => {
        // VÉRIFICATION DE L'AUTHENTIFICATION
        if (!isAuthenticated) {
            console.error('Non authentifié pour charger les enrichissements');
            return [];
        }

        try {
            const response = await apiClient(`/damia/messages/${messageId}/enrichments`);

            if (!response.ok) {
                if (response.status === 401) {
                    console.error('Token expiré, déconnexion...');
                    logout();
                }
                return [];
            }

            const data = await response.json();

            if (data.success && data.data.enrichments.jurisprudence.length > 0) {
                return data.data.enrichments.jurisprudence.map((juris: any) => ({
                    source: 'jurisprudence',
                    title: juris.title,
                    content: `${juris.title} - ${juris.jurisdiction}`,
                    relevanceScore: juris.relevanceScore,
                    isSimplified: false,
                    jurisprudenceId: juris.jurisprudenceId,
                    jurisdiction: juris.jurisdiction,
                    chamber: juris.chamber,
                    number: juris.number,
                    decision_date: juris.decision_date,
                    solution: juris.solution,
                    legalDomain: juris.legalDomain,
                    issues: juris.primaryIssues,
                    hasBaseData: juris.hasBaseData,
                    hasMetadata: juris.hasMetadata,
                    confidenceScore: juris.confidenceScore
                }));
            }

            return [];
        } catch (error) {
            console.error(`Erreur chargement enrichissements:`, error);
            return [];
        }
    };

    // VÉRIFICATION DE L'AUTHENTIFICATION AVANT TOUTE REQUÊTE
    useEffect(() => {
        if (!isAuthenticated) {
            setError('Vous devez être connecté pour accéder à Damia');
            setLoading(false);
            return;
        }

        const fetchConversations = async () => {
            try {
                const response = await apiClient(`/damia/conversations`);

                if (!response.ok) {
                    if (response.status === 401) {
                        console.error('Token invalide (401), déconnexion...');
                        logout();
                        return;
                    }
                    throw new Error(`Erreur lors de la récupération des conversations: ${response.status}`);
                }

                const data = await response.json();

                const formattedConversations = data.conversations.map((conv: any) => ({
                    id: conv._id,
                    title: conv.title,
                    lastMessageDate: new Date(conv.lastActivity || conv.createdAt),
                    messages: []
                }));

                setConversations(formattedConversations);

                if (formattedConversations.length > 0 && !currentConversationId) {
                    setCurrentConversationId(formattedConversations[0].id);
                }
            } catch (err) {
                console.error('Erreur récupération conversations:', err);
                setError(err instanceof Error ? err.message : 'Une erreur est survenue');
            } finally {
                setLoading(false);
            }
        };

        fetchConversations();
    }, [isAuthenticated]);

    // Récupérer les messages de la conversation sélectionnée
    useEffect(() => {
        if (!currentConversationId || !isAuthenticated) return;

        const fetchMessages = async () => {
            try {
                const response = await apiClient(`/damia/conversations/${currentConversationId}/messages`);

                if (!response.ok) {
                    if (response.status === 401) {
                        logout();
                        return;
                    }
                    throw new Error('Erreur lors de la récupération des messages');
                }

                const data = await response.json();

                const formattedMessages = data.messages.map((msg: any) => ({
                    id: msg._id,
                    sender: msg.role,
                    content: msg.content,
                    timestamp: new Date(msg.createdAt),
                    searchParams: msg.enrichments,
                    searchResults: msg.enrichmentResults?.length > 0 ?
                        mapEnrichmentResults(msg.enrichmentResults) : undefined,
                    feedbackStatus: msg.feedbackStatus || null,
                    regenerated: msg.regenerated || false,
                    parentMessageId: msg.parentMessageId || undefined,
                    thinkingSteps: msg.thinkingSteps || undefined
                }));

                // Charger automatiquement les enrichissements pour les messages avec jurisprudences simplifiées
                const enrichMessagesPromises = formattedMessages.map(async (msg: any) => {
                    const hasSimplifiedJurisprudence = msg.searchResults?.some((result: any) =>
                        result.source === 'jurisprudence' && result.isSimplified
                    );

                    if (hasSimplifiedJurisprudence && msg.sender === 'assistant') {
                        const enrichedJurisprudences = await loadEnrichedJurisprudences(msg.id);

                        if (enrichedJurisprudences.length > 0) {
                            const updatedSearchResults = msg.searchResults?.map((result: any) => {
                                if (result.source === 'jurisprudence' && result.isSimplified) {
                                    const enriched = enrichedJurisprudences.find((enr: any) =>
                                        enr.jurisprudenceId === result.jurisprudenceId
                                    );
                                    return enriched || result;
                                }
                                return result;
                            }) || [];

                            return { ...msg, searchResults: updatedSearchResults };
                        }
                    }

                    return msg;
                });

                const enrichedMessages = await Promise.all(enrichMessagesPromises);

                setConversations(prev =>
                    prev.map(conv =>
                        conv.id === currentConversationId
                            ? { ...conv, messages: enrichedMessages }
                            : conv
                    )
                );
            } catch (err) {
                console.error('Erreur lors du chargement des messages:', err);
            }
        };

        fetchMessages();
    }, [currentConversationId, isAuthenticated]);

    const handleSendMessage = async (content: string, searchParams?: { legifrance: boolean; jurisprudence: boolean; hal: boolean }) => {
        if (!currentConversationId || !isAuthenticated) return;

        resetThinking();

        // Ajouter immédiatement le message utilisateur
        const tempUserMessage: Message = {
            id: `temp-${Date.now()}`,
            sender: 'user',
            content: content,
            timestamp: new Date(),
            searchParams: searchParams
        };

        setConversations(prev =>
            prev.map(conv =>
                conv.id === currentConversationId
                    ? {
                        ...conv,
                        messages: [...conv.messages, tempUserMessage],
                        lastMessageDate: new Date()
                    }
                    : conv
            )
        );

        setIsThinking(true);

        try {
            const response = await apiClient(`/damia/conversations/${currentConversationId}/messages`, {
                method: 'POST',
                body: JSON.stringify({
                    content,
                    enrichments: searchParams
                })
            });

            if (!response.ok) {
                if (response.status === 401) {
                    logout();
                    return;
                }
                throw new Error('Erreur lors de l\'envoi du message');
            }

            const data = await response.json();

            const userMessage: Message = {
                id: data.userMessage._id,
                sender: 'user',
                content: content,
                timestamp: new Date(data.userMessage.createdAt),
                searchParams: searchParams
            };

            // Mapper les enrichissements puis enrichir immédiatement les jurisprudences
            let searchResults = data.assistantMessage.enrichmentResults
                ? mapEnrichmentResults(data.assistantMessage.enrichmentResults)
                : undefined;

            // Enrichir les jurisprudences simplifiées immédiatement via l'API
            if (searchResults && searchResults.some((r: any) => r.source === 'jurisprudence' && r.isSimplified)) {
                searchResults = await enrichSimplifiedJurisprudences(searchResults);
            }

            const assistantMessage: Message = {
                id: data.assistantMessage._id,
                sender: 'assistant',
                content: data.assistantMessage.content,
                timestamp: new Date(data.assistantMessage.createdAt),
                searchResults,
                thinkingSteps: data.assistantMessage.thinkingSteps || undefined
            };

            setConversations(prev =>
                prev.map(conv =>
                    conv.id === currentConversationId
                        ? {
                            ...conv,
                            messages: [
                                ...conv.messages.filter(msg => msg.id !== tempUserMessage.id),
                                userMessage,
                                assistantMessage
                            ],
                            lastMessageDate: new Date(),
                            ...(data.newTitle && { title: data.newTitle })
                        }
                        : conv
                )
            );

            // Si un nouveau titre a été généré, déclencher l'animation de frappe
            if (data.newTitle && currentConversationId) {
                setTypingTitle({ conversationId: currentConversationId, title: data.newTitle });
            }

        } catch (err) {
            console.error('Erreur lors de l\'envoi du message:', err);
            setConversations(prev =>
                prev.map(conv =>
                    conv.id === currentConversationId
                        ? {
                            ...conv,
                            messages: conv.messages.filter(msg => msg.id !== tempUserMessage.id)
                        }
                        : conv
                )
            );
        } finally {
            setIsThinking(false);
        }
    };

    const handleNewConversation = async () => {
        if (!isAuthenticated || isCreatingConversation) return;

        // Si la conversation courante est vide, on change juste le message d'accueil
        const currentConv = conversations.find(c => c.id === currentConversationId);
        if (currentConv && currentConv.messages.length === 0) {
            setWelcomeMessageIndex(prev => prev + 1);
            return;
        }

        setIsCreatingConversation(true);
        try {
            const response = await apiClient(`/damia/conversations`, {
                method: 'POST',
                body: JSON.stringify({
                    title: `Conversation du ${new Date().toLocaleDateString()}`
                })
            });

            if (!response.ok) {
                if (response.status === 401) {
                    logout();
                    return;
                }
                if (response.status === 429) {
                    console.warn('Trop de conversations vides récentes');
                    setWelcomeMessageIndex(prev => prev + 1);
                    return;
                }
                throw new Error('Erreur lors de la création de la conversation');
            }

            const newConversation = await response.json();

            const formattedConversation: Conversation = {
                id: newConversation._id,
                title: newConversation.title,
                lastMessageDate: new Date(newConversation.createdAt),
                messages: []
            };

            setConversations(prev => [formattedConversation, ...prev]);
            setCurrentConversationId(newConversation._id);
            setWelcomeMessageIndex(0);
        } catch (err) {
            console.error('Erreur lors de la création de la conversation:', err);
        } finally {
            setIsCreatingConversation(false);
        }
    };

    const handleRenameConversation = async (conversationId: string, newTitle: string) => {
        if (!isAuthenticated) return;

        try {
            const response = await apiClient(`/damia/conversations/${conversationId}`, {
                method: 'PATCH',
                body: JSON.stringify({ title: newTitle })
            });

            if (!response.ok) {
                if (response.status === 401) {
                    logout();
                    return;
                }
                throw new Error('Erreur lors du renommage de la conversation');
            }

            setConversations(prev =>
                prev.map(conv =>
                    conv.id === conversationId
                        ? { ...conv, title: newTitle }
                        : conv
                )
            );
        } catch (err) {
            console.error('Erreur lors du renommage:', err);
        }
    };

    const handleArchiveConversation = async (conversationId: string) => {
        if (!isAuthenticated) return;

        try {
            const response = await apiClient(`/damia/conversations/${conversationId}/archive`, {
                method: 'PATCH',
            });

            if (!response.ok) {
                if (response.status === 401) {
                    logout();
                    return;
                }
                throw new Error('Erreur lors de l\'archivage de la conversation');
            }

            setConversations(prev => prev.filter(conv => conv.id !== conversationId));

            if (currentConversationId === conversationId) {
                setCurrentConversationId(null);
            }
        } catch (err) {
            console.error('Erreur lors de l\'archivage:', err);
        }
    };

    const handleMessageFeedback = async (messageId: string, isPositive: boolean | null) => {
        if (!currentConversationId || !isAuthenticated) return;

        try {
            const feedbackStatus = isPositive === null ? null : isPositive ? 'positive' : 'negative';

            const response = await apiClient(`/damia/messages/${messageId}/feedback`, {
                method: 'PATCH',
                body: JSON.stringify({
                    feedbackStatus
                })
            });

            if (!response.ok) {
                if (response.status === 401) {
                    logout();
                    return;
                }
                throw new Error('Erreur lors de l\'envoi du feedback');
            }

            setConversations(prev =>
                prev.map(conv =>
                    conv.id === currentConversationId
                        ? {
                            ...conv,
                            messages: conv.messages.map(msg =>
                                msg.id === messageId
                                    ? { ...msg, feedbackStatus }
                                    : msg
                            )
                        }
                        : conv
                )
            );
        } catch (err) {
            console.error('Erreur lors de l\'envoi du feedback:', err);
        }
    };

    const handleRetryMessage = async (messageId: string) => {
        if (!currentConversationId || !isAuthenticated) return;

        resetThinking();

        const currentConv = conversations.find(c => c.id === currentConversationId);
        if (!currentConv) return;

        const messageToRetry = currentConv.messages.find(m => m.id === messageId);
        if (!messageToRetry || messageToRetry.sender !== 'assistant') return;

        const userMessageIndex = currentConv.messages.findIndex(m => m.id === messageId) - 1;
        if (userMessageIndex < 0) return;

        const userMessage = currentConv.messages[userMessageIndex];

        setIsThinking(true);

        try {
            const response = await apiClient(`/damia/messages/${messageId}/regenerate`, {
                method: 'POST',
                body: JSON.stringify({
                    parentMessageId: messageId,
                    userMessageId: userMessage.id,
                    enrichments: userMessage.searchParams
                })
            });

            if (!response.ok) {
                if (response.status === 401) {
                    logout();
                    return;
                }
                throw new Error('Erreur lors de la régénération du message');
            }

            const data = await response.json();

            // Mapper et enrichir les jurisprudences immédiatement
            let searchResults = data.enrichmentResults
                ? mapEnrichmentResults(data.enrichmentResults)
                : undefined;

            if (searchResults && searchResults.some((r: any) => r.source === 'jurisprudence' && r.isSimplified)) {
                searchResults = await enrichSimplifiedJurisprudences(searchResults);
            }

            setConversations(prev =>
                prev.map(conv =>
                    conv.id === currentConversationId
                        ? {
                            ...conv,
                            messages: [
                                ...conv.messages,
                                {
                                    id: data._id,
                                    sender: 'assistant',
                                    content: data.content,
                                    timestamp: new Date(data.createdAt),
                                    searchResults,
                                    parentMessageId: messageId,
                                    regenerated: true
                                }
                            ],
                            lastMessageDate: new Date()
                        }
                        : conv
                )
            );

        } catch (err) {
            console.error('Erreur lors de la régénération du message:', err);
        } finally {
            setIsThinking(false);
        }
    };

    const currentConversation = conversations.find(c => c.id === currentConversationId) || null;

    // Générer le message d'accueil personnalisé
    const welcomeMessage = useMemo(() => {
        return getWelcomeMessage(firstName, welcomeMessageIndex);
    }, [firstName, welcomeMessageIndex]);

    // AFFICHAGE D'ERREUR SI NON AUTHENTIFIÉ
    if (!isAuthenticated) {
        return (
            <div className="h-screen flex flex-col bg-white dark:bg-mcdm-third-500 overflow-hidden">
                <TopBar />
                <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                        <p className="text-red-500 mb-4">Vous devez être connecté pour accéder à Damia</p>
                        <button
                            onClick={() => window.location.href = '/login'}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                        >
                            Se connecter
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="h-screen flex flex-col bg-white dark:bg-mcdm-third-500 overflow-hidden">
                <TopBar />
                <div className="flex items-center justify-center h-full">
                    <p className="text-mcdm-third-400 dark:text-mcdm-primary-300">Chargement...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="h-screen flex flex-col bg-white dark:bg-mcdm-third-500 overflow-hidden">
                <TopBar />
                <div className="flex items-center justify-center h-full">
                    <p className="text-red-500">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen flex flex-col bg-white dark:bg-mcdm-third-500 overflow-hidden">
            <TopBar />

            <div className="flex h-[calc(100vh)] pt-16">
                <DamiaSidebar
                    conversations={conversations}
                    currentConversationId={currentConversationId}
                    onSelectConversation={setCurrentConversationId}
                    onNewConversation={handleNewConversation}
                    isCollapsed={isSidebarCollapsed}
                    onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                    onRenameConversation={handleRenameConversation}
                    onArchiveConversation={handleArchiveConversation}
                    typingTitle={typingTitle}
                    onTypingComplete={() => setTypingTitle(null)}
                />

                <div className="flex-1">
                    {currentConversation ? (
                        <ChatArea
                            messages={currentConversation.messages}
                            onSendMessage={handleSendMessage}
                            onMessageFeedback={handleMessageFeedback}
                            onRetry={handleRetryMessage}
                            isThinking={isThinking}
                            thinkingSteps={thinkingSteps}
                            isThinkingActive={isThinkingActive}
                            welcomeMessage={welcomeMessage}
                        />
                    ) : (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center text-mcdm-third-400 dark:text-mcdm-primary-300">
                                <h3 className="text-xl font-platypi mb-2">Bienvenue sur Damia</h3>
                                <p>Commencez une nouvelle conversation ou sélectionnez-en une existante</p>
                                <button
                                    onClick={handleNewConversation}
                                    className="mt-4 px-4 py-2 bg-mcdm-secondary-400 hover:bg-mcdm-secondary-500 
                           text-white rounded-md transition-colors duration-200"
                                >
                                    Nouvelle conversation
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Damia;